/**
 * factura-mode.ts — Lógica pura para el formulario de facturación modo cliente.
 *
 * Sin dependencias React. Testeable con Vitest + fast-check.
 */

import type { OrdenDisponible } from "@/hooks/use-facturacion";

// =============================================================================
// Types
// =============================================================================

export type ModoFactura = "orden" | "paciente" | "cliente";

export interface FormStateFactura {
  selectedIds: number[];
  nombreCliente: string;
  rtnCliente: string;
}

export interface FacturaCreatePayload {
  orden_ids: number[];
  nombre_cliente: string;
  rtn_cliente: string;
  descuento_global: number;
  observaciones: string;
}

export interface GrupoOrdenPorPaciente {
  paciente_nombre: string;
  ordenes: OrdenDisponible[];
}

// =============================================================================
// resetFormState
// =============================================================================

/** Retorna un estado de formulario vacío. */
export function resetFormState(): FormStateFactura {
  return { selectedIds: [], nombreCliente: "", rtnCliente: "" };
}

// =============================================================================
// autoFillCliente
// =============================================================================

/**
 * Rellena nombre_cliente y rtn_cliente desde el objeto cliente
 * solo si los campos actuales están vacíos.
 */
export function autoFillCliente(
  cliente: { nombre: string; rtn?: string | null },
  current: FormStateFactura
): FormStateFactura {
  return {
    ...current,
    nombreCliente: current.nombreCliente || cliente.nombre,
    rtnCliente: current.rtnCliente || (cliente.rtn ?? ""),
  };
}

// =============================================================================
// agruparPorPaciente
// =============================================================================

/**
 * Agrupa un array de OrdenDisponible por paciente_nombre.
 * El orden de los grupos sigue el orden de primera aparición.
 * Órdenes con paciente_nombre vacío van al grupo "".
 */
export function agruparPorPaciente(ordenes: OrdenDisponible[]): GrupoOrdenPorPaciente[] {
  const map = new Map<string, OrdenDisponible[]>();

  for (const orden of ordenes) {
    const key = orden.paciente_nombre ?? "";
    if (!map.has(key)) {
      map.set(key, []);
    }
    map.get(key)!.push(orden);
  }

  return Array.from(map.entries()).map(([paciente_nombre, ords]) => ({
    paciente_nombre,
    ordenes: ords,
  }));
}

// =============================================================================
// autoFillOrden
// =============================================================================

/**
 * Rellena nombreCliente y rtnCliente desde la orden si tiene cliente asociado.
 * Siempre pone selectedIds con el id de la orden.
 */
export function autoFillOrden(orden: OrdenDisponible, current: FormStateFactura): FormStateFactura {
  if (orden.cliente_id != null) {
    return {
      ...current,
      selectedIds: [orden.id],
      nombreCliente: orden.cliente_nombre ?? "",
      rtnCliente: orden.cliente_rtn ?? "",
    };
  }
  return {
    ...current,
    selectedIds: [orden.id],
    nombreCliente: "",
    rtnCliente: "",
  };
}

// =============================================================================
// buildFacturaPayload
// =============================================================================

// =============================================================================
// resolveNombreCliente
// =============================================================================

/**
 * Resuelve el nombre_cliente final a enviar al backend.
 *
 * - Si clienteOpcional no está vacío (tras trim) → usa clienteOpcional
 * - Si está vacío → usa nombrePaciente
 *
 * Lógica pura, sin dependencias React. Testeable con Vitest + fast-check.
 */
export function resolveNombreCliente(clienteOpcional: string, nombrePaciente: string): string {
  return clienteOpcional.trim() || nombrePaciente.trim();
}

// =============================================================================
// buildFacturaPayload
// =============================================================================

/**
 * Construye el payload para crear una factura a partir del estado del formulario.
 */
export function buildFacturaPayload(
  state: FormStateFactura,
  extras: { descuentoGlobal?: string; observaciones?: string }
): FacturaCreatePayload {
  return {
    orden_ids: state.selectedIds,
    nombre_cliente: state.nombreCliente,
    rtn_cliente: state.rtnCliente,
    descuento_global: parseFloat(extras.descuentoGlobal ?? "0"),
    observaciones: extras.observaciones ?? "",
  };
}
